import { InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Skeleton } from '@/components/ui/skeleton';

import { formatMetricValue } from './formatMetric';
import { ResolvedMetric } from './metricConfig';

interface Props {
  /** groupMetrics().disputes — disputes then dispute_rate. */
  disputes: ResolvedMetric[];
  /** groupMetrics().exclusions — stopped. */
  exclusions: ResolvedMetric[];
  isLoading?: boolean;
}

interface GroupProps {
  title: string;
  metrics: ResolvedMetric[];
}

const BarrierGroup = ({ title, metrics }: GroupProps) => {
  const { t } = useTranslation();

  if (metrics.length === 0) {
    return null;
  }

  return (
    <div>
      <h3 className="text-sm leading-5 font-medium text-foreground">{title}</h3>
      <div className="mt-2 border-b border-stroke" />
      {metrics.map((metric) => (
        <div className="flex items-center justify-between py-2" key={metric.key}>
          <div className="flex items-center gap-1">
            <span className="text-sm text-foreground-muted">{t(metric.labelKey)}</span>
            {metric.tooltipKey ? (
              <InfoTooltip content={t(metric.tooltipKey)} size="large">
                <InfoIcon className="size-3 shrink-0 text-foreground-muted" />
              </InfoTooltip>
            ) : null}
          </div>
          <span className="text-sm font-semibold text-foreground">
            {formatMetricValue(metric.value, metric.format)}
          </span>
        </div>
      ))}
    </div>
  );
};

export const BarriersToProgress = ({ disputes, exclusions, isLoading }: Props) => {
  const { t } = useTranslation();

  const heading = (
    <h2 className="mb-4 text-base leading-6 font-semibold text-foreground">
      {t('campaigns.performance.sections.barriers')}
    </h2>
  );

  if (isLoading) {
    return (
      <section>
        {heading}
        <Skeleton className="h-24 w-full rounded-lg" />
      </section>
    );
  }

  if (disputes.length === 0 && exclusions.length === 0) {
    return null;
  }

  return (
    <section>
      {heading}
      <div className="grid gap-6 lg:grid-cols-2">
        <BarrierGroup metrics={disputes} title={t('campaigns.performance.barriers.disputes')} />
        <BarrierGroup metrics={exclusions} title={t('campaigns.performance.barriers.exclusions')} />
      </div>
    </section>
  );
};
